import React, { useCallback, useMemo, useState, useEffect } from "react";
import { ApolloError } from "@apollo/client";
import Container from "@mui/material/Container";
import { Form, Input, Select, InputNumber, Modal, Slider } from "antd";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
  useGetWorkspaceLocationsByWorkspaceIdQuery,
  GetWorkspaceLocationsByWorkspaceIdQuery,
} from "../../graphql/queries/getWorkspaceLocationsByWorkspaceId.generated";
import { useAddMentorshipProgramMutation } from "../../graphql/mutations/addMentorshipProgram.generated";
import {
  MentorshipProgramModule_Insert_Input,
  MentorshipProgram_Insert_Input,
} from "../../graphql/generated/graphql";

import { useAuth } from "../../context/AuthContext";
import routeNames from "../../constants/route-names";
import { SurveyAvailability } from "../../constants/input-values";

type Option = { value: string | number | null; label: string };

export const surveyOptions = Object.values(SurveyAvailability).map(
  (element) => {
    return { value: element, label: element };
  }
);

// Returns the default shape for a module at a given position (1-based order)
export const defaultModuleAt = (
  index: number
): MentorshipProgramModule_Insert_Input => ({
  name: ``,
  order: index + 1,
  duration: 14,
  content: "",
});

export function SortableModuleItem({
  i,
  m,
  isLoading,
  updateModule,
  removeModule,
}: {
  i: number;
  m: MentorshipProgramModule_Insert_Input;
  isLoading: boolean;
  updateModule: (
    index: number,
    patch: Partial<MentorshipProgramModule_Insert_Input>
  ) => void;
  removeModule?: (index: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: i });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="mentorship-form-section module"
    >
      <img
        src={process.env.PUBLIC_URL + "/assets/images/arrows-move.svg"}
        alt="arrows move icon"
        className="arrows-move-icon"
        {...attributes}
        {...listeners}
        style={{ cursor: "grab" }}
      />

      <div className="module-header-row">
        <span className="mentorship-form-section-title">{`Check-In ${m.order}`}</span>
      </div>

      <Form.Item
        className="mentorship-form-item"
        rules={[{ required: true, message: "Please input the Module Topic." }]}
        required
        validateTrigger="onBlur"
      >
        <Input
          name="topic"
          placeholder="Topic"
          className="mentorship-form-input"
          value={m.name ?? ""}
          autoComplete="off"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            updateModule(i, { name: event.target.value });
          }}
          allowClear
          disabled={isLoading}
        />
      </Form.Item>

      <Form.Item
        className="mentorship-form-item"
        rules={[
          {
            required: true,
            message: "Please input the Module Talking Points.",
          },
        ]}
        required
        validateTrigger="onBlur"
      >
        <Input.TextArea
          name="content"
          placeholder="Talking Points"
          className="mentorship-form-input"
          value={m.content ?? ""}
          autoComplete="off"
          onChange={(event) => {
            updateModule(i, { content: event.target.value });
          }}
          allowClear
          disabled={isLoading}
          style={{ paddingTop: "10px" }}
        />
      </Form.Item>

      <span className="module-duration-title">{`Duration`}</span>
      <p className="module-duration-description">
        {`How many days between this Check-In's start date and the next Check-In's start date.`}
      </p>

      <Slider
        className="mentorship-form-item"
        range
        min={0}
        max={45}
        step={1}
        value={[0, m.duration ?? 14]} // controlado: el primero siempre 0
        tooltip={{
          open: true,
          formatter: (v) => {
            if (v === 0) return `0`;
            if (v === 1) return `1 day`;
            return `${v} days`;
          },
        }}
        onChange={(value: number | number[]) => {
          const [, end] = value as [number, number];
          updateModule(i, { duration: end });
        }}
        onChangeComplete={(value: number | number[]) => {
          // opcional
        }}
      />

      {removeModule ? (
        <div className="remove-module-icon-container">
          <img
            src={
              process.env.PUBLIC_URL + "/assets/images/remove-trash-garbage.svg"
            }
            alt="remove module icon"
            className="remove-module-icon"
            onClick={() => removeModule(i)}
          />
        </div>
      ) : null}
    </div>
  );
}

export default function MentorshipProgramAdd() {
  const { loggedUserData } = useAuth();

  const navigate = useNavigate();

  const [workspaceId, setWorkspaceId] = useState<number>(0);
  const [name, setName] = useState<string>("");
  const [mentorLabel, setMentorLabel] = useState<string>("");
  const [menteeLabel, setMenteeLabel] = useState<string>("");
  const [matchingMethod, setMatchingMethod] = useState<string | null>(null);
  const [modulesQuantity, setModulesQuantity] = useState<number | null>(null);
  const [availability, setAvailability] = useState<number | null>(null);

  const [preSurveyLink, setPreSurveyLink] = useState<string>("");
  const [preSurveyAvailability, setPreSurveyAvailability] = useState<
    string | null
  >(null);
  const [midSurveyLink, setMidSurveyLink] = useState<string>("");
  const [midSurveyAvailability, setMidSurveyAvailability] = useState<
    string | null
  >(null);
  const [postSurveyLink, setPostSurveyLink] = useState<string>("");
  const [postSurveyAvailability, setPostSurveyAvailability] = useState<
    string | null
  >(null);
  const [modules, setModules] = useState<
    Array<MentorshipProgramModule_Insert_Input>
  >([]);

  useEffect(() => {
    if (loggedUserData?.workspaces?.length) {
      setWorkspaceId(loggedUserData.workspaces[0].workspace.id);
    }
  }, [loggedUserData]);

  /**
   * Keeps the `modules` array in sync with `modulesQuantity`.
   * Behavior:
   * - If there are more modules than needed, truncate and fix `order`.
   * - If there are fewer, add new modules with default values and correct `order`.
   * - Always ensure `order` is 1..N.
   */
  useEffect(() => {
    setModules((previousModules) => {
      const desiredCount = modulesQuantity || 0;

      // Start from a shallow copy so we can mutate locally step-by-step
      const adjusted = [...previousModules];

      // A) Too many modules -> remove extras from the end
      if (adjusted.length > desiredCount) {
        adjusted.length = desiredCount; // truncate
      }

      // B) Too few modules -> push new default modules until reaching desiredCount
      if (adjusted.length < desiredCount) {
        const currentLength = adjusted.length;
        const missing = desiredCount - currentLength;

        for (let offset = 0; offset < missing; offset++) {
          const newIndex = currentLength + offset; // 0-based index for the new item
          adjusted.push(defaultModuleAt(newIndex));
        }
      }

      // C) Normalize `order` to be 1..N (safe even if names/durations were edited)
      for (let i = 0; i < adjusted.length; i++) {
        adjusted[i] = { ...adjusted[i], order: i + 1 };
      }

      return adjusted;
    });
  }, [modulesQuantity]);

  const updateModule = useCallback(
    (index: number, patch: Partial<MentorshipProgramModule_Insert_Input>) => {
      setModules((prev) => {
        const copy = [...prev];
        copy[index] = { ...copy[index], ...patch };
        return copy;
      });
    },
    []
  );

  // Compares the current module vs its default to decide if it has user edits
  const isModuleEdited = (
    module: MentorshipProgramModule_Insert_Input,
    index: number
  ) => {
    const defaults = defaultModuleAt(index);
    const hasNameChange = (module.name ?? "") !== (defaults.name ?? "");
    const hasDurationChange =
      (module.duration ?? 14) !== (defaults.duration ?? 14);
    const hasContentChange =
      (module.content ?? "") !== (defaults.content ?? "");
    return hasNameChange || hasDurationChange || hasContentChange;
  };

  /**
   * Removes a single module at the provided index.
   * - If the module has been edited (differs from its defaults), ask for confirmation.
   * - After deletion, reindex orders and keep `modulesQuantity` consistent.
   */
  const removeModule = (index: number) => {
    // Guard: invalid index — do nothing
    if (index < 0 || index >= modules.length) return;

    const executeRemoval = () => {
      // 1) Build a new array without the removed item
      const nextModules = modules.filter((_, i) => i !== index);

      // 2) Reassign 1-based `order` sequentially
      for (let i = 0; i < nextModules.length; i++) {
        nextModules[i] = { ...nextModules[i], order: i + 1 };
      }

      // 3) Commit to state
      setModules(nextModules);

      // 4) Keep the quantity state in sync
      setModulesQuantity((current) => {
        if (current) {
          return Math.max(0, current - 1);
        }
        return 0;
      });
    };

    // Ask for confirmation only when the module differs from its default values
    if (isModuleEdited(modules[index], index)) {
      Modal.confirm({
        title: "Remove module?",
        content:
          "This module contains entered data. Removing it will permanently delete those changes.",
        okText: "Remove",
        okButtonProps: { color: "primary" },
        type: "confirm",
        cancelText: "Cancel",
        onOk: executeRemoval,
      });
    } else {
      executeRemoval();
    }
  };

  const handleOnCompletedGetWorkspaceLocationsByWorkspaceId = useCallback(
    (data: GetWorkspaceLocationsByWorkspaceIdQuery) => {
      // console.log("Get Workspace Locations: ", data);
    },
    []
  );

  const handleOnErrorsGetWorkspaceLocationsByWorkspaceId = useCallback(
    (error: ApolloError) => {
      console.log("Get Workspace Locations error: ", error);
    },
    []
  );

  const { data: workspaceLocationsData, loading: workspaceLocationsLoading } =
    useGetWorkspaceLocationsByWorkspaceIdQuery({
      skip: !workspaceId,
      variables: {
        workspaceId: workspaceId as any,
      },
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
      errorPolicy: "none",
      notifyOnNetworkStatusChange: true,
      onCompleted: handleOnCompletedGetWorkspaceLocationsByWorkspaceId,
      onError: handleOnErrorsGetWorkspaceLocationsByWorkspaceId,
    });

  const handleOnCompletedAddMentorshipProgram = useCallback((data: any) => {
    console.log("Add MentorshipProgram Completed: ", data);

    navigate(routeNames.MENTORSHIP);
  }, []);

  const handleOnErrorsAddMentorshipProgram = useCallback(
    (error: ApolloError) => {
      console.log("Add MentorshipProgram error: ", error);
    },
    []
  );

  const [addMentorshipProgramMutation, { data, loading, error }] =
    useAddMentorshipProgramMutation({
      errorPolicy: "none",
      notifyOnNetworkStatusChange: true,
      onCompleted: handleOnCompletedAddMentorshipProgram,
      onError: handleOnErrorsAddMentorshipProgram,
    });

  const handleOnSubmit = useCallback(async () => {
    if (loggedUserData && name && workspaceId) {
      const mutationVariables: MentorshipProgram_Insert_Input = {
        createdByUserId: loggedUserData?.id,
        name: name,
        workspaceId: workspaceId,
        workspaceLocationId: availability || null,
        mentorLabel: mentorLabel || null,
        menteeLabel: menteeLabel || null,
        matchingMethod: matchingMethod || null
      };

      if (modules && modules.length) {
        const modulesData = modules.map((element) => {
          return {
            content: element.content,
            createdByUserId: loggedUserData?.id,
            duration: element.duration,
            name: element.name,
            order: element.order,
          };
        });

        mutationVariables.mentorshipProgramModules = {
          data: modulesData,
        };
      }

      mutationVariables.mentorshipSurveys = {
        data: [
          {
            createdByUserId: loggedUserData?.id,
            preSurveyEnabled: !!preSurveyLink,
            preSurveyLink: preSurveyLink,
            preSurveyMentorRequired:
              preSurveyAvailability === SurveyAvailability.BOTH ||
              preSurveyAvailability === SurveyAvailability.MENTORS,
            preSurveyMenteeRequired:
              preSurveyAvailability === SurveyAvailability.BOTH ||
              preSurveyAvailability === SurveyAvailability.MENTEES,
            midSurveyEnabled: !!midSurveyLink,
            midSurveyLink: midSurveyLink,
            midSurveyMentorRequired:
              midSurveyAvailability === SurveyAvailability.BOTH ||
              midSurveyAvailability === SurveyAvailability.MENTORS,
            midSurveyMenteeRequired:
              midSurveyAvailability === SurveyAvailability.BOTH ||
              midSurveyAvailability === SurveyAvailability.MENTEES,
            postSurveyEnabled: !!postSurveyLink,
            postSurveyLink: postSurveyLink,
            postSurveyMentorRequired:
              postSurveyAvailability === SurveyAvailability.BOTH ||
              postSurveyAvailability === SurveyAvailability.MENTORS,
            postSurveyMenteeRequired:
              postSurveyAvailability === SurveyAvailability.BOTH ||
              postSurveyAvailability === SurveyAvailability.MENTEES,
          },
        ],
      };

      console.log("handleOnSubmit mutationVariables: ", mutationVariables);

      addMentorshipProgramMutation({
        variables: {
          object: mutationVariables,
        },
      });
    }
  }, [
    loggedUserData,
    name,
    mentorLabel,
    menteeLabel,
    matchingMethod,
    workspaceId,
    availability,
    modules,
    preSurveyLink,
    preSurveyAvailability,
    midSurveyLink,
    midSurveyAvailability,
    postSurveyLink,
    postSurveyAvailability,
  ]);

  const availabilityOptions = useMemo<Option[]>(() => {
    const defaultOptions: Option[] = [
      { value: null, label: "All mentors and mentees in my organization" },
    ];

    if (workspaceLocationsData?.workspaceLocations.length) {
      const locations: Option[] =
        workspaceLocationsData?.workspaceLocations.map((element) => {
          return { value: element.id, label: element.name };
        });

      return [...defaultOptions, ...locations];
    }

    return defaultOptions;
  }, [workspaceLocationsData]);

  const matchingMethodOptions = useMemo<Option[]>(() => {
    const defaultOptions: Option[] = [
      { value: null, label: "Matching Method" },
      { value: "userRequested", label: "User Requested" },
      { value: "adminManaged", label: "Admin Managed" },
    ];

    return defaultOptions;
  }, [workspaceLocationsData]);

  const preSurveyLinkRequired = useMemo(
    () =>
      preSurveyAvailability != null &&
      preSurveyAvailability !== SurveyAvailability.NONE,
    [preSurveyAvailability]
  );

  const midSurveyLinkRequired = useMemo(
    () =>
      midSurveyAvailability != null &&
      midSurveyAvailability !== SurveyAvailability.NONE,
    [midSurveyAvailability]
  );

  const postSurveyLinkRequired = useMemo(
    () =>
      postSurveyAvailability != null &&
      postSurveyAvailability !== SurveyAvailability.NONE,
    [postSurveyAvailability]
  );

  const isLoading = loading;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // We use indexes as ids (not persisted; only UI)
    const oldIndex = Number(active.id);
    const newIndex = Number(over.id);

    setModules((prev) => {
      const reordered = arrayMove(prev, oldIndex, newIndex);
      // reindex order 1..N
      return reordered.map((m, idx) => ({ ...m, order: idx + 1 }));
    });
  };

  return (
    <Container component="main" maxWidth={false}>
      <div className="mentorship-row">
        <h1>New Program</h1>
      </div>
      <div className="mentorship-form-section">
        <span className="mentorship-form-section-title">Program Details</span>
        <span className={"input-label"}>Program Name</span>
        <span className={"input-label-sub"}>What would you like to name this program? For example, RN Residency Mentorship</span>
        <Form.Item
          className="mentorship-form-item"
          rules={[
            { required: true, message: "Please input the Program name." },
          ]}
          required
          validateTrigger="onBlur"
        >
          <Input
            name="name"
            placeholder="Program Name"
            className="mentorship-form-input"
            value={name}
            autoComplete="off"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setName(event.target.value);
            }}
            allowClear
            disabled={isLoading}
          />
        </Form.Item>
        <span className={"input-label"}>Role Labels</span>
        <span className={"input-label-sub"}>How should mentor and mentee roles be labeled? For example, Mentor and Mentee, or Preceptor and Resident.</span>
        <Form.Item
          className="mentorship-form-item"
          rules={[{ required: true, message: "Please input the mentor label" }]}
          required
          validateTrigger="onBlur"
        >
          <Input
            name="mentor-label"
            placeholder="Mentor Label"
            className="mentorship-form-input"
            value={mentorLabel}
            autoComplete="off"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setMentorLabel(event.target.value);
            }}
            allowClear
            disabled={isLoading}
          />
        </Form.Item>
        <Form.Item
          className="mentorship-form-item"
          rules={[{ required: true, message: "Please input the mentee label" }]}
          required
          validateTrigger="onBlur"
        >
          <Input
            name="mentee-label"
            placeholder="Mentee Label"
            className="mentorship-form-input"
            value={menteeLabel}
            autoComplete="off"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setMenteeLabel(event.target.value);
            }}
            allowClear
            disabled={isLoading}
          />
        </Form.Item>        
        <span className={"input-label"}>Matching Method</span>
        <span className={"input-label-sub"}>How should program participants be matched? If Self-Selected, participants can send, receive, and review mentorship requests from colleagues. If Admin Managed, you will match participants after this program has been created. </span>
        <Form.Item
          className="mentorship-form-item"
          rules={[
            {
              required: true,
              message: "Please select matching method",
            },
          ]}
          required
          validateTrigger="onBlur"
        >
          <Select
            value={matchingMethod}
            className="mentorship-form-input small"
            onChange={(value) => setMatchingMethod(value)}
            options={matchingMethodOptions}
            placeholder="Matching Method"
            allowClear
            disabled={isLoading}
            suffixIcon={
              <img
                src={
                  process.env.PUBLIC_URL +
                  "/assets/images/black-chevron-down.svg"
                }
                alt="dropdown icon"
                className="chevron-button"
              />
            }
          />
        </Form.Item>
        <span className={"input-label"}>Availability</span>
        <span className={"input-label-sub"}>A program can be limited to a single location, or available to all eligible participants across the organization.</span>
        <Form.Item
          className="mentorship-form-item"
          rules={[
            {
              required: true,
              message: "Please select the Location Availability",
            },
          ]}
          required
          validateTrigger="onBlur"
        >
          <Select
            value={availability}
            className="mentorship-form-input small"
            onChange={(value) => setAvailability(value)}
            options={availabilityOptions}
            placeholder="Location Availability"
            allowClear
            disabled={workspaceLocationsLoading}
            suffixIcon={
              <img
                src={
                  process.env.PUBLIC_URL +
                  "/assets/images/black-chevron-down.svg"
                }
                alt="dropdown icon"
                className="chevron-button"
              />
            }
          />
        </Form.Item>
        <span className={"input-label"}>{'Surveys (Optional)'}</span>
        <span className={"input-label-sub"}>For each survey, specify which participants should be prompted, and provide a valid link.</span>
        <div className="mentorship-input-row">
          <Form.Item
            className="mentorship-form-paired-item"
            validateTrigger="onBlur"
          >
            <Select
              value={preSurveyAvailability}
              className="mentorship-form-input small"
              onChange={(value) => setPreSurveyAvailability(value)}
              options={surveyOptions}
              placeholder="Pre Survey"
              allowClear
              disabled={workspaceLocationsLoading}
              suffixIcon={
                <img
                  src={
                    process.env.PUBLIC_URL +
                    "/assets/images/black-chevron-down.svg"
                  }
                  alt="dropdown icon"
                  className="chevron-button"
                />
              }
            />
          </Form.Item>

          <Form.Item
            className="mentorship-form-paired-item"
            validateTrigger="onBlur"
            rules={[
              {
                required: preSurveyLinkRequired,
                message: "Please enter the Pre Survey URL.",
              },
            ]}
            required={preSurveyLinkRequired}
            validateStatus={
              preSurveyLinkRequired && !preSurveyLink ? "error" : ""
            }
            help={
              preSurveyLinkRequired && !preSurveyLink
                ? "Please enter the Pre Survey URL."
                : undefined
            }
          >
            <Input
              name="preSurveyLink"
              placeholder="If yes, enter URL here"
              className="mentorship-form-input paired"
              value={preSurveyLink}
              autoComplete="off"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setPreSurveyLink(event.target.value);
              }}
              allowClear
              required={preSurveyLinkRequired}
              disabled={isLoading}
            />
          </Form.Item>
        </div>

        <div className="mentorship-input-row">
          <Form.Item
            className="mentorship-form-paired-item"
            validateTrigger="onBlur"
          >
            <Select
              value={midSurveyAvailability}
              className="mentorship-form-input small"
              onChange={(value) => setMidSurveyAvailability(value)}
              options={surveyOptions}
              placeholder="Mid Survey"
              allowClear
              disabled={workspaceLocationsLoading}
              suffixIcon={
                <img
                  src={
                    process.env.PUBLIC_URL +
                    "/assets/images/black-chevron-down.svg"
                  }
                  alt="dropdown icon"
                  className="chevron-button"
                />
              }
            />
          </Form.Item>

          <Form.Item
            className="mentorship-form-paired-item"
            validateTrigger="onBlur"
            rules={[
              {
                required: midSurveyLinkRequired,
                message: "Please enter the Mid Survey URL.",
              },
            ]}
            required={midSurveyLinkRequired}
            validateStatus={
              midSurveyLinkRequired && !midSurveyLink ? "error" : ""
            }
            help={
              midSurveyLinkRequired && !midSurveyLink
                ? "Please enter the Mid Survey URL."
                : undefined
            }
          >
            <Input
              name="midSurveyLink"
              placeholder="If yes, enter URL here"
              className="mentorship-form-input paired"
              value={midSurveyLink}
              autoComplete="off"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setMidSurveyLink(event.target.value);
              }}
              allowClear
              required={midSurveyLinkRequired}
              disabled={isLoading}
            />
          </Form.Item>
        </div>

        <div className="mentorship-input-row">
          <Form.Item
            className="mentorship-form-paired-item"
            validateTrigger="onBlur"
          >
            <Select
              value={postSurveyAvailability}
              className="mentorship-form-input small"
              onChange={(value) => setPostSurveyAvailability(value)}
              options={surveyOptions}
              placeholder="Post Survey"
              allowClear
              disabled={workspaceLocationsLoading}
              suffixIcon={
                <img
                  src={
                    process.env.PUBLIC_URL +
                    "/assets/images/black-chevron-down.svg"
                  }
                  alt="dropdown icon"
                  className="chevron-button"
                />
              }
            />
          </Form.Item>

          <Form.Item
            className="mentorship-form-paired-item"
            validateTrigger="onBlur"
            rules={[
              {
                required: postSurveyLinkRequired,
                message: "Please enter the Post Survey URL.",
              },
            ]}
            required={postSurveyLinkRequired}
            validateStatus={
              postSurveyLinkRequired && !postSurveyLink ? "error" : ""
            }
            help={
              postSurveyLinkRequired && !postSurveyLink
                ? "Please enter the Post Survey URL."
                : undefined
            }
          >
            <Input
              name="postSurveyLink"
              placeholder="If yes, enter URL here"
              className="mentorship-form-input paired"
              value={postSurveyLink}
              autoComplete="off"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setPostSurveyLink(event.target.value);
              }}
              allowClear
              required={postSurveyLinkRequired}
              disabled={isLoading}
            />
          </Form.Item>
        </div>
        <span className={"input-label"}>Number of Check-Ins</span>
        <span className={"input-label-sub"}>Specify the number of scheduled Check-Ins for program participants to complete. Check-In details can be managed below.</span>
        <Form.Item
          className="mentorship-form-item"
          rules={[
            {
              required: true,
              message: "Please select the Number of Check-ins",
            },
          ]}
          required
          validateTrigger="onBlur"
        >
          <InputNumber
            className="mentorship-form-input small"
            min={1}
            max={20}
            onChange={(value: number | null) => {
              if (value) {
                setModulesQuantity(value);
              }
            }}
            placeholder="Number of Check-Ins"
            value={modulesQuantity}
            disabled={isLoading}
          />
        </Form.Item>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={modules.map((_, idx) => idx)}
          strategy={verticalListSortingStrategy}
        >
          {modules.map((m, i) => (
            <SortableModuleItem
              key={i}
              i={i}
              m={m}
              isLoading={isLoading}
              updateModule={updateModule}
              removeModule={removeModule}
            />
          ))}
        </SortableContext>
      </DndContext>

      <div className="mentorship-form-buttons">
        <button
          className="mentorship-cancel-button"
          onClick={() => navigate(routeNames.MENTORSHIP)}
        >
          Cancel
        </button>
        <button className="mentorship-save-button" onClick={handleOnSubmit}>
          Save
        </button>
      </div>
    </Container>
  );
}
